import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import RobotFace from "@/components/RobotFace";
import { toast } from "sonner";

export function PaymentSuccess() {
  const [sp] = useSearchParams();
  const sid = sp.get("session_id");
  const [state, setState] = useState({ phase: "checking", plan: null });
  const { refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!sid) { setState({ phase: "notfound" }); return; }
    let attempts = 0;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState({ phase: "paid", plan: data.target_plan, credits: data.credits });
          window.dispatchEvent(new Event("rammy-credits-changed"));
          toast.success(data.credits
            ? `${Number(data.credits).toLocaleString()} credits added.`
            : `You're on the ${data.target_plan} plan.`);
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setState({ phase: "failed" }); return;
        }
      } catch (e) {
        if (e.response?.status === 404) { setState({ phase: "notfound" }); return; }
      }
      if (attempts > 20) { setState({ phase: "timeout" }); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, [sid, refresh]);

  const phase = state.phase;
  return (
    <div className="min-h-screen flex items-center justify-center p-8" data-testid="page-payment-success">
      <div className="max-w-md text-center">
        <RobotFace expression={phase === "paid" ? "excited" : phase === "checking" ? "thinking" : "sad"} size={200}/>
        <h1 className="mt-6 text-3xl font-display">
          {phase === "checking" && "Confirming your payment…"}
          {phase === "paid" && "You're in."}
          {phase === "failed" && "Payment didn't go through."}
          {phase === "notfound" && "Can't find this payment."}
          {phase === "timeout" && "Still waiting for Stripe…"}
        </h1>
        {phase === "checking" && <div className="mt-3 inline-flex items-center gap-2 text-sm text-[var(--rammy-text-muted)]"><Loader2 size={14} className="animate-spin"/>Talking to Stripe</div>}
        {phase === "paid" && (
          <>
            <p className="mt-3 text-[var(--rammy-text-sec)]">
              {state.credits
                ? `${Number(state.credits).toLocaleString()} credits are in your account. They're good for the next 12 months.`
                : `Your ${state.plan} features are unlocked. Welcome to a warmer Rammy.`}
            </p>
            <div className="mt-6 flex justify-center gap-2">
              <Button onClick={()=>nav("/app")} className="btn-pill btn-primary" data-testid="payment-success-open-app">Open the app</Button>
            </div>
          </>
        )}
        {(phase === "failed" || phase === "notfound" || phase === "timeout") && (
          <>
            <p className="mt-3 text-[var(--rammy-text-sec)]">If your card was charged, refresh in a minute — Stripe sometimes takes a moment.</p>
            <div className="mt-6 flex justify-center gap-2">
              <Link to="/app"><Button className="btn-pill btn-primary" data-testid="payment-back-app">Back to Rammy</Button></Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen flex items-center justify-center p-8" data-testid="page-payment-cancel">
      <div className="max-w-md text-center">
        <RobotFace expression="curious" size={200}/>
        <h1 className="mt-6 text-3xl font-display">No worries — nothing charged.</h1>
        <p className="mt-3 text-[var(--rammy-text-sec)]">You can upgrade anytime from Settings › Subscription.</p>
        <div className="mt-6 flex justify-center gap-2">
          <Link to="/app"><Button className="btn-pill btn-primary" data-testid="payment-cancel-back">Back to Rammy</Button></Link>
        </div>
      </div>
    </div>
  );
}

export function UpgradeButton({ packageId, label, testid, className = "" }) {
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast.error("Could not start checkout");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };
  return (
    <Button onClick={start} disabled={busy} className={`btn-pill btn-primary ${className}`} data-testid={testid}>
      {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <Sparkles size={14} className="mr-1"/>}
      {busy ? "Redirecting…" : label}
    </Button>
  );
}
